/**
 * Quick-form field definitions for the 35 core clinical assessments (catalog slugs).
 * Delirium (4AT) uses a dedicated scored UI in ClinicalAssessmentQuickFormModal.
 * Full clinical logic: Compliance.md §6-7 + Resident.md §11
 */

import { clinicalAssessmentCatalog } from './clinicalAssessmentCatalog';

/** @typedef {{ value: string, label: string }} QuickFormOption */
/** @typedef {{ id: string, label: string, type: 'select' | 'text' | 'textarea' | 'number', options?: QuickFormOption[], placeholder?: string, min?: number, max?: number, step?: number, required?: boolean, rows?: number }} QuickFormField */
/** @typedef {{ intro?: string, fields: QuickFormField[] }} QuickFormDefinition */

/** @param {string} v @param {string} l @returns {QuickFormOption} */
const o = (v, l) => ({ value: v, label: l });
const YES_NO = [o('yes', 'Yes'), o('no', 'No')];
const YES_NO_NA = [o('yes', 'Yes'), o('no', 'No'), o('na', 'N/A')];
const RISK_BAND = [o('low', 'Low'), o('moderate', 'Moderate'), o('high', 'High'), o('very_high', 'Very high')];
const SEVERITY = [o('none', 'None / N/A'), o('mild', 'Mild'), o('moderate', 'Moderate'), o('severe', 'Severe')];
const STATUS_DRAFT = [o('draft', 'Draft'), o('final', 'Final — ready for care plan')];

/** @type {Record<string, QuickFormDefinition>} */
export const CLINICAL_QUICK_FORM_DEFINITIONS = {
  'my-life-story': {
    intro: 'Capture identity and preferences for person-centred care.',
    fields: [
      { id: 'preferredName', label: 'Preferred name / how to address', type: 'text', required: true, placeholder: 'e.g. Mrs Chen, Betty' },
      { id: 'lifeNarrative', label: 'Life story highlights', type: 'textarea', rows: 3, required: true, placeholder: 'Background, roles, milestones' },
      { id: 'preferences', label: 'Likes, dislikes, routines', type: 'textarea', rows: 3, required: true },
      { id: 'relationships', label: 'Important people & contact preferences', type: 'textarea', rows: 2, required: false },
      { id: 'familyPortal', label: 'Family engaged via portal?', type: 'select', options: YES_NO, required: true },
    ],
  },
  'falls-risk': {
    intro: 'Falls risk screen — link to prevention plan.',
    fields: [
      { id: 'fallsPast12mo', label: 'Falls in last 12 months', type: 'select', options: [o('0', '0'), o('1', '1'), o('2plus', '2+')], required: true },
      { id: 'walkingAid', label: 'Walking aid used', type: 'select', options: [o('none', 'None'), o('stick', 'Stick'), o('frame', 'Frame'), o('wheelchair', 'Wheelchair')], required: true },
      { id: 'tugSeconds', label: 'TUG time (seconds), if done', type: 'number', min: 0, max: 300, step: 1, required: false },
      { id: 'riskBand', label: 'Overall risk band', type: 'select', options: RISK_BAND, required: true },
      { id: 'actions', label: 'Immediate actions / referrals', type: 'textarea', rows: 2, required: false },
    ],
  },
  'pressure-injury-risk': {
    intro: 'Braden-style capture — align with full scale in spec.',
    fields: [
      { id: 'bradenTotal', label: 'Braden total score (6–23)', type: 'number', min: 6, max: 23, required: true },
      { id: 'riskBand', label: 'Interpreted risk', type: 'select', options: [o('low', 'Low (15–23)'), o('moderate', 'Moderate (13–14)'), o('high', 'High (10–12)'), o('very_high', 'Very high (≤9)')], required: true },
      { id: 'equipment', label: 'Pressure care equipment', type: 'text', placeholder: 'Mattress, cushion, boots…' },
      { id: 'repositioning', label: 'Repositioning plan noted?', type: 'select', options: YES_NO, required: true },
      { id: 'notes', label: 'Sites / skin notes', type: 'textarea', rows: 2 },
    ],
  },
  'nutritional-screening': {
    intro: 'Malnutrition screen — MNA-SF or service equivalent.',
    fields: [
      { id: 'mnaSfScore', label: 'MNA-SF score (0–14)', type: 'number', min: 0, max: 14, required: true },
      { id: 'risk', label: 'Nutrition risk', type: 'select', options: [o('normal', 'Normal status'), o('at_risk', 'At risk'), o('malnourished', 'Malnourished')], required: true },
      { id: 'weightKg', label: 'Weight (kg)', type: 'number', min: 20, max: 250, step: 0.1, required: false },
      { id: 'dietitianReferral', label: 'Dietitian referral?', type: 'select', options: YES_NO, required: true },
      { id: 'plan', label: 'Nutrition actions', type: 'textarea', rows: 2 },
    ],
  },
  'cognitive-assessment': {
    intro: 'Screening tool result — formal diagnosis via GP/Geriatrics.',
    fields: [
      { id: 'tool', label: 'Tool used', type: 'select', options: [o('mmse', 'MMSE'), o('moca', 'MoCA'), o('rudas', 'RUDAS'), o('other', 'Other')], required: true },
      { id: 'score', label: 'Score', type: 'number', min: 0, max: 30, required: true },
      { id: 'maxScore', label: 'Max for tool', type: 'number', min: 1, max: 100, required: true },
      { id: 'capacityFlag', label: 'Capacity concern flagged?', type: 'select', options: YES_NO, required: true },
      { id: 'communication', label: 'Communication strategies', type: 'textarea', rows: 2 },
    ],
  },
  'depression-screening': {
    intro: 'GDS / Cornell or service-approved screen.',
    fields: [
      { id: 'tool', label: 'Tool', type: 'select', options: [o('gds15', 'GDS-15'), o('cornell', 'Cornell'), o('other', 'Other')], required: true },
      { id: 'score', label: 'Score', type: 'number', min: 0, max: 30, required: true },
      { id: 'interpretation', label: 'Interpretation', type: 'select', options: [o('normal', 'Within normal'), o('mild', 'Mild'), o('moderate', 'Moderate'), o('severe', 'Severe / urgent')], required: true },
      { id: 'safety', label: 'Safety / self-harm concern?', type: 'select', options: YES_NO, required: true },
      { id: 'followUp', label: 'Follow-up plan', type: 'textarea', rows: 2 },
    ],
  },
  'pain-assessment': {
    intro: 'Pain at rest / movement — effectiveness of PRN.',
    fields: [
      { id: 'scale', label: 'Scale', type: 'select', options: [o('nrs', 'NRS 0–10'), o('abbey', 'Abbey'), o('other', 'Other / observational')], required: true },
      { id: 'score', label: 'Score / level', type: 'number', min: 0, max: 10, required: true },
      { id: 'location', label: 'Site / character', type: 'text', required: false },
      { id: 'prnGiven', label: 'PRN given this shift?', type: 'select', options: YES_NO_NA, required: true },
      { id: 'effective60', label: 'Effective within 60 min?', type: 'select', options: YES_NO_NA, required: false },
    ],
  },
  'continence-assessment': {
    intro: 'Bladder / bowel, aids, skin.',
    fields: [
      { id: 'bladder', label: 'Bladder pattern', type: 'select', options: [o('continent', 'Continent'), o('urge', 'Urge'), o('functional', 'Functional'), o('retention', 'Retention concern'), o('catheter', 'Catheter')], required: true },
      { id: 'bowel', label: 'Bowel pattern', type: 'select', options: [o('regular', 'Regular'), o('constipation', 'Constipation'), o('diarrhoea', 'Diarrhoea'), o('stoma', 'Stoma')], required: true },
      { id: 'aids', label: 'Pads / appliances', type: 'text' },
      { id: 'toiletingPlan', label: 'Toileting plan in place?', type: 'select', options: YES_NO, required: true },
      { id: 'periSkin', label: 'Peri-skin notes', type: 'textarea', rows: 2 },
    ],
  },
  'skin-integrity-body-map': {
    intro: 'Whole-body skin check — link to wound care if needed.',
    fields: [
      { id: 'overall', label: 'Overall skin status', type: 'select', options: [o('intact', 'Intact'), o('at_risk', 'At risk'), o('damage', 'Damage present')], required: true },
      { id: 'sites', label: 'Sites documented (text / map ref)', type: 'textarea', rows: 2, required: true },
      { id: 'bradenLinked', label: 'PI risk assessment linked?', type: 'select', options: YES_NO, required: true },
      { id: 'actions', label: 'Actions', type: 'textarea', rows: 2 },
    ],
  },
  'oral-health': {
    intro: 'Oral cavity screen — referral pathway.',
    fields: [
      { id: 'teethDentures', label: 'Teeth / dentures', type: 'select', options: [o('good', 'Satisfactory'), o('issues', 'Issues'), o('edentulous', 'Edentulous — mucosa check')], required: true },
      { id: 'mouthPain', label: 'Oral pain?', type: 'select', options: YES_NO, required: true },
      { id: 'dryMouth', label: 'Dry mouth / saliva', type: 'select', options: SEVERITY, required: true },
      { id: 'dentalReferral', label: 'Dental referral needed?', type: 'select', options: YES_NO, required: true },
      { id: 'notes', label: 'Notes', type: 'textarea', rows: 2 },
    ],
  },
  'mobility-functional': {
    intro: 'Transfers, aids, function — cross-ref falls.',
    fields: [
      { id: 'transfer', label: 'Transfer ability', type: 'select', options: [o('indep', 'Independent'), o('supervised', 'Supervised'), o('assist1', '1 assist'), o('assist2', '2+ assist'), o('hoist', 'Hoist')], required: true },
      { id: 'aids', label: 'Mobility aids', type: 'text' },
      { id: 'barthelApprox', label: 'Barthel band (if used)', type: 'select', options: [o('low', 'Low dependence'), o('mod', 'Moderate'), o('high', 'High independence')], required: false },
      { id: 'therapyRef', label: 'PT / OT referral?', type: 'select', options: YES_NO, required: true },
      { id: 'goals', label: 'Goals', type: 'textarea', rows: 2 },
    ],
  },
  'sensory-assessment': {
    intro: 'Vision, hearing, aids.',
    fields: [
      { id: 'vision', label: 'Vision', type: 'select', options: [o('normal', 'Adequate'), o('impaired', 'Impaired'), o('blind', 'Blind / light perception')], required: true },
      { id: 'hearing', label: 'Hearing', type: 'select', options: [o('normal', 'Adequate'), o('impaired', 'Impaired'), o('deaf', 'Deaf / CI')], required: true },
      { id: 'glasses', label: 'Glasses used?', type: 'select', options: YES_NO_NA, required: true },
      { id: 'hearingAid', label: 'Hearing aid used?', type: 'select', options: YES_NO_NA, required: true },
      { id: 'strategies', label: 'Communication strategies', type: 'textarea', rows: 2 },
    ],
  },
  'medication-reconciliation': {
    intro: 'Sources aligned — high-risk meds flagged.',
    fields: [
      { id: 'sources', label: 'Sources reconciled', type: 'textarea', rows: 2, placeholder: 'GP, hospital DC, community pharmacy…', required: true },
      { id: 'discrepancies', label: 'Discrepancies found?', type: 'select', options: YES_NO, required: true },
      { id: 'discrepancyDetail', label: 'Discrepancy detail', type: 'textarea', rows: 2 },
      { id: 'highRisk', label: 'High-risk class present (opioid, anticoag, insulin…)?', type: 'select', options: YES_NO, required: true },
      { id: 'pharmacyNotified', label: 'Pharmacist / GP notified?', type: 'select', options: YES_NO_NA, required: true },
    ],
  },
  'behaviour-assessment': {
    intro: 'ABC, triggers — BSP link.',
    fields: [
      { id: 'episode', label: 'Behaviour of concern (brief)', type: 'text', required: true },
      { id: 'antecedent', label: 'Antecedents / triggers', type: 'textarea', rows: 2, required: true },
      { id: 'consequence', label: 'Consequences / interventions tried', type: 'textarea', rows: 2 },
      { id: 'bspExists', label: 'BSP in place or required?', type: 'select', options: [o('exists', 'BSP exists'), o('needed', 'BSP needed'), o('review', 'BSP review')], required: true },
      { id: 'restrictive', label: 'Restrictive practice involved?', type: 'select', options: YES_NO, required: true },
    ],
  },
  'dysphagia-swallowing': {
    intro: 'Aspiration risk — IDDSI alignment.',
    fields: [
      { id: 'screenResult', label: 'Screen result', type: 'select', options: [o('normal', 'No concern'), o('at_risk', 'At risk'), o('failed', 'Failed screen')], required: true },
      { id: 'iddsiFood', label: 'IDDSI food level (current)', type: 'select', options: [o('7', 'Level 7 Regular'), o('6', 'Level 6'), o('5', 'Level 5'), o('4', 'Level 4'), o('3', 'Level 3'), o('na', 'N/A')], required: true },
      { id: 'iddsiFluid', label: 'IDDSI fluid level', type: 'select', options: [o('0', 'Level 0'), o('1', '1'), o('2', '2'), o('3', '3'), o('4', '4'), o('na', 'N/A')], required: true },
      { id: 'slpReferral', label: 'SLP referral?', type: 'select', options: YES_NO, required: true },
      { id: 'positioning', label: 'Meal positioning notes', type: 'textarea', rows: 2 },
    ],
  },
  'sleep-assessment': {
    intro: 'Pattern, disturbances, meds.',
    fields: [
      { id: 'quality', label: 'Sleep quality', type: 'select', options: SEVERITY, required: true },
      { id: 'fragmented', label: 'Fragmented sleep / night waking?', type: 'select', options: YES_NO, required: true },
      { id: 'daySleep', label: 'Excessive day sleep?', type: 'select', options: YES_NO, required: true },
      { id: 'sleepMeds', label: 'Sleep-related medications reviewed?', type: 'select', options: YES_NO, required: true },
      { id: 'plan', label: 'Non-pharm / environment plan', type: 'textarea', rows: 2 },
    ],
  },
  'social-emotional-wellbeing': {
    intro: 'Engagement, adjustment, loneliness.',
    fields: [
      { id: 'mood', label: 'Observed mood', type: 'select', options: [o('stable', 'Stable / positive'), o('low', 'Low mood'), o('anxious', 'Anxious'), o('mixed', 'Mixed')], required: true },
      { id: 'engagement', label: 'Engagement in activities', type: 'select', options: [o('good', 'Good'), o('fair', 'Fair'), o('poor', 'Poor')], required: true },
      { id: 'loneliness', label: 'Loneliness / isolation concern?', type: 'select', options: YES_NO, required: true },
      { id: 'adjustment', label: 'Adjustment to residential care', type: 'select', options: [o('good', 'Good'), o('ongoing', 'Ongoing'), o('poor', 'Poor')], required: true },
      { id: 'actions', label: 'Wellbeing actions', type: 'textarea', rows: 2 },
    ],
  },
  'spiritual-cultural-needs': {
    intro: 'Faith, culture, pastoral.',
    fields: [
      { id: 'faith', label: 'Faith / spiritual practice important?', type: 'select', options: YES_NO_NA, required: true },
      { id: 'cultural', label: 'Cultural care needs', type: 'textarea', rows: 2 },
      { id: 'dietaryCulture', label: 'Cultural dietary requirements', type: 'text' },
      { id: 'pastoral', label: 'Pastoral / clergy contact arranged?', type: 'select', options: YES_NO_NA, required: false },
      { id: 'notes', label: 'Notes', type: 'textarea', rows: 2 },
    ],
  },
  'wound-assessment': {
    intro: 'Per-wound snapshot — full charting in wound module.',
    fields: [
      { id: 'location', label: 'Wound location', type: 'text', required: true },
      { id: 'stage', label: 'Stage / category', type: 'select', options: [o('stage1', 'Stage 1'), o('stage2', 'Stage 2'), o('stage3', 'Stage 3'), o('stage4', 'Stage 4'), o('unstageable', 'Unstageable'), o('other', 'Other / surgical')], required: true },
      { id: 'exudate', label: 'Exudate', type: 'select', options: [o('none', 'None'), o('low', 'Low'), o('mod', 'Moderate'), o('high', 'High')], required: true },
      { id: 'infectionSigns', label: 'Infection signs?', type: 'select', options: YES_NO, required: true },
      { id: 'plan', label: 'Dressing / review plan', type: 'textarea', rows: 2 },
    ],
  },
  'restraint-assessment': {
    intro: 'Least restrictive — alternatives documented.',
    fields: [
      { id: 'type', label: 'Type of concern / practice', type: 'text', required: true },
      { id: 'alternatives', label: 'Alternatives trialled', type: 'textarea', rows: 2, required: true },
      { id: 'authorised', label: 'Authorisation pathway commenced?', type: 'select', options: YES_NO, required: true },
      { id: 'monitoring', label: 'Monitoring plan', type: 'textarea', rows: 2 },
      { id: 'reviewDue', label: 'Review due (free text date)', type: 'text', placeholder: 'e.g. 2026-05-01' },
    ],
  },
  'palliative-care-needs': {
    intro: 'Surprise question, symptoms, goals.',
    fields: [
      { id: 'surprise', label: 'Would you be surprised if the resident died in 12 months?', type: 'select', options: [o('yes', 'Yes (surprised)'), o('no', 'No (not surprised)')], required: true },
      { id: 'symptomBurden', label: 'Symptom burden', type: 'select', options: SEVERITY, required: true },
      { id: 'advanceCare', label: 'ACP / goals documented?', type: 'select', options: YES_NO_NA, required: true },
      { id: 'medications', label: 'Anticipatory meds discussed?', type: 'select', options: YES_NO_NA, required: false },
      { id: 'notes', label: 'Palliative plan notes', type: 'textarea', rows: 2 },
    ],
  },
  'advance-care-planning': {
    intro: 'ACD, SDM, treatment preferences.',
    fields: [
      { id: 'acd', label: 'Advance Care Directive on file?', type: 'select', options: YES_NO_NA, required: true },
      { id: 'sdm', label: 'Substitute decision-maker named?', type: 'select', options: YES_NO_NA, required: true },
      { id: 'hospital', label: 'Hospitalisation preference discussed?', type: 'select', options: YES_NO_NA, required: true },
      { id: 'cpcr', label: 'CPR / escalation preferences', type: 'textarea', rows: 2 },
      { id: 'reviewDate', label: 'Review date', type: 'text', placeholder: 'YYYY-MM-DD' },
    ],
  },
  'goals-of-care': {
    intro: 'Structured goals — owner and status.',
    fields: [
      { id: 'goal1', label: 'Primary goal', type: 'text', required: true },
      { id: 'status1', label: 'Status', type: 'select', options: [o('not_started', 'Not started'), o('in_progress', 'In progress'), o('met', 'Met'), o('review', 'Needs review')], required: true },
      { id: 'owner', label: 'Responsible role', type: 'text', placeholder: 'RN, EN, Physio…' },
      { id: 'goal2', label: 'Secondary goal (optional)', type: 'text' },
      { id: 'residentInvolved', label: 'Resident / representative involved?', type: 'select', options: YES_NO, required: true },
    ],
  },
  'psychotropic-review': {
    intro: 'Indication, BSP, effectiveness.',
    fields: [
      { id: 'medClass', label: 'Class reviewed', type: 'select', options: [o('antipsych', 'Antipsychotic'), o('benzo', 'Benzodiazepine'), o('hypnotic', 'Hypnotic'), o('other', 'Other psychotropic')], required: true },
      { id: 'indication', label: 'Clear indication documented?', type: 'select', options: YES_NO, required: true },
      { id: 'bsp', label: 'Linked to BSP where required?', type: 'select', options: YES_NO_NA, required: true },
      { id: 'trialReduction', label: 'Reduction trial discussed?', type: 'select', options: YES_NO_NA, required: true },
      { id: 'outcome', label: 'Review outcome', type: 'textarea', rows: 2 },
    ],
  },
  'antimicrobial-stewardship': {
    intro: 'Indication, duration, review.',
    fields: [
      { id: 'agent', label: 'Antimicrobial (name)', type: 'text', required: true },
      { id: 'indication', label: 'Indication', type: 'text', required: true },
      { id: 'days', label: 'Days on therapy', type: 'number', min: 0, max: 365, required: false },
      { id: 'reviewDue', label: 'Stop / review date', type: 'text', required: true },
      { id: 'ivOral', label: 'IV / oral step-down plan?', type: 'select', options: [o('na', 'N/A'), o('planned', 'Planned'), o('done', 'Done')], required: true },
    ],
  },
  'infection-control-assessment': {
    intro: 'Precautions, isolation, outbreak.',
    fields: [
      { id: 'precaution', label: 'Precaution level', type: 'select', options: [o('standard', 'Standard'), o('contact', 'Contact'), o('droplet', 'Droplet'), o('airborne', 'Airborne')], required: true },
      { id: 'isolation', label: 'Isolation required?', type: 'select', options: YES_NO, required: true },
      { id: 'outbreak', label: 'Outbreak / cluster?', type: 'select', options: YES_NO, required: true },
      { id: 'ppe', label: 'PPE education done?', type: 'select', options: YES_NO_NA, required: true },
      { id: 'notes', label: 'IPC notes', type: 'textarea', rows: 2 },
    ],
  },
  'hand-hygiene-audit': {
    intro: 'Observation audit — WHO moments.',
    fields: [
      { id: 'opportunities', label: 'Opportunities observed', type: 'number', min: 0, max: 500, required: true },
      { id: 'compliant', label: 'Compliant moments', type: 'number', min: 0, max: 500, required: true },
      { id: 'area', label: 'Area / unit', type: 'text', required: true },
      { id: 'auditor', label: 'Auditor role', type: 'text' },
      { id: 'actions', label: 'Corrective actions', type: 'textarea', rows: 2 },
    ],
  },
  'clinical-frailty-scale': {
    intro: 'Rockwood CFS 1–9.',
    fields: [
      { id: 'cfsScore', label: 'CFS score (1–9)', type: 'number', min: 1, max: 9, required: true },
      { id: 'trajectory', label: 'Trajectory vs last review', type: 'select', options: [o('stable', 'Stable'), o('worse', 'Worse'), o('better', 'Improved')], required: true },
      { id: 'palliativeFlag', label: 'Triggers palliative / care intensity review?', type: 'select', options: YES_NO, required: true },
      { id: 'notes', label: 'Functional notes', type: 'textarea', rows: 2 },
    ],
  },
  'wellbeing-qol-survey': {
    intro: 'Resident-reported experience (summary).',
    fields: [
      { id: 'satisfaction', label: 'Overall satisfaction', type: 'select', options: [o('5', 'Very satisfied'), o('4', 'Satisfied'), o('3', 'Neutral'), o('2', 'Dissatisfied'), o('1', 'Very dissatisfied')], required: true },
      { id: 'lonely', label: 'Loneliness items — concern?', type: 'select', options: YES_NO, required: true },
      { id: 'participation', label: 'Participation in home life', type: 'select', options: [o('good', 'Good'), o('fair', 'Fair'), o('poor', 'Poor')], required: true },
      { id: 'followUp', label: 'Follow-up actions', type: 'textarea', rows: 2 },
    ],
  },
  'iad-assessment': {
    intro: 'Perineal skin related to incontinence.',
    fields: [
      { id: 'severity', label: 'IAD severity', type: 'select', options: SEVERITY, required: true },
      { id: 'area', label: 'Affected area', type: 'text', required: true },
      { id: 'barrierCream', label: 'Barrier regimen in place?', type: 'select', options: YES_NO, required: true },
      { id: 'continencePlan', label: 'Continence plan updated?', type: 'select', options: YES_NO, required: true },
      { id: 'review', label: 'Review date', type: 'text' },
    ],
  },
  'iddsi-assessment': {
    intro: 'Texture levels — kitchen notified.',
    fields: [
      { id: 'foodLevel', label: 'Food texture (IDDSI)', type: 'select', options: [o('7', '7'), o('6', '6'), o('5', '5'), o('4', '4'), o('3', '3')], required: true },
      { id: 'fluidLevel', label: 'Fluid level', type: 'select', options: [o('0', '0'), o('1', '1'), o('2', '2'), o('3', '3'), o('4', '4')], required: true },
      { id: 'testMethod', label: 'Testing method noted', type: 'text' },
      { id: 'kitchenNotified', label: 'Kitchen / catering notified?', type: 'select', options: YES_NO, required: true },
      { id: 'notes', label: 'Notes', type: 'textarea', rows: 2 },
    ],
  },
  'restraint-authorisation-review': {
    intro: 'Formal authorisation — tied to BSP.',
    fields: [
      { id: 'authorisationId', label: 'Authorisation reference / ID', type: 'text' },
      { id: 'expiry', label: 'Authorisation expiry / review due', type: 'text', required: true },
      { id: 'continuedNeed', label: 'Continued need justified?', type: 'select', options: YES_NO, required: true },
      { id: 'lessRestrictive', label: 'Less restrictive options re-trialled?', type: 'select', options: YES_NO, required: true },
      { id: 'signOff', label: 'Reviewer sign-off (role)', type: 'text', required: true },
    ],
  },
  'pre-admission-clinical-screen': {
    intro: 'CRM stage — capability before offer.',
    fields: [
      { id: 'clinicalFit', label: 'Clinical fit for service', type: 'select', options: [o('suitable', 'Suitable'), o('conditional', 'Conditional'), o('not_suitable', 'Not suitable')], required: true },
      { id: 'riskRating', label: 'Pre-admission risk rating', type: 'select', options: RISK_BAND, required: true },
      { id: 'dependencies', label: 'Care dependencies summary', type: 'textarea', rows: 2, required: true },
      { id: 'referrals', label: 'Referrals needed pre-admission', type: 'textarea', rows: 2 },
      { id: 'notes', label: 'Handoff notes for admission', type: 'textarea', rows: 2 },
    ],
  },
  'comprehensive-initial-assessment': {
    intro: 'Master sign-off after individual assessments complete.',
    fields: [
      { id: 'individualsComplete', label: 'Individual assessments complete?', type: 'select', options: YES_NO, required: true },
      { id: 'risksSummarised', label: 'Key risks summarised in care plan?', type: 'select', options: YES_NO, required: true },
      { id: 'referrals', label: 'Outstanding referrals', type: 'textarea', rows: 2 },
      { id: 'rnSignOff', label: 'RN sign-off name / ID', type: 'text', required: true },
      { id: 'status', label: 'Record status', type: 'select', options: STATUS_DRAFT, required: true },
    ],
  },
};

/**
 * @param {string} slug
 * @returns {QuickFormDefinition | null}
 */
export function getQuickFormDefinition(slug) {
  return CLINICAL_QUICK_FORM_DEFINITIONS[slug] ?? null;
}

/**
 * Slugs that use a bespoke modal (not the generic quick form).
 */
export const QUICK_FORM_SPECIAL_SLUGS = new Set(['delirium-4at']);

/**
 * @returns {string[]} Catalog slugs missing a generic quick form (excludes special slugs).
 */
export function getQuickFormCoverageGaps() {
  return clinicalAssessmentCatalog
    .map((a) => a.slug)
    .filter((s) => !QUICK_FORM_SPECIAL_SLUGS.has(s) && !CLINICAL_QUICK_FORM_DEFINITIONS[s]);
}
